import { HttpClient } from "@angular/common/http";
import { ChangeDetectorRef, Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, finalize, map } from "rxjs/operators";
import { Globals } from "../../app.globals";

@Injectable()
export class ContactsService {
    private apiUrl = "/api/settings/contacts";

    constructor(public http: HttpClient, public globals: Globals, private ctx: ChangeDetectorRef) {}

    getAllNotInProjectByOrganizationId(
        projectId: number,
        organizationId: number
    ): Observable<Contact[]> {
        this.showSpinner();
        return this.http
            .get(this.apiUrl + "/" + projectId + "/" + organizationId)
            .pipe(
                map((res) => res as Contact[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getContact(contactId: number): Observable<Contact> {
        this.showSpinner();
        return this.http.get(this.apiUrl + "/" + contactId).pipe(
            map((res) => res as Contact),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    getContacts(): Observable<Contact[]> {
        this.showSpinner();
        return this.http.get(this.apiUrl + "/getall").pipe(
            map((res) => res as Contact[]),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    addContactToProject(projectId: number, contactIds: any): Observable<any> {
        this.showSpinner();
        return this.http
            .post(this.apiUrl + "/" + projectId, { contactIds: contactIds })
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    updateContact(contactId: number, contact: any): Observable<any> {
        this.showSpinner();
        return this.http.put(this.apiUrl + "/" + contactId, contact).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    uploadFile(formData: FormData): Observable<any> {
        this.showSpinner();
        return this.http.put(this.apiUrl + "/upload", formData).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    addContact(contact: any): Observable<any> {
        this.showSpinner();
        return this.http.post(this.apiUrl, contact).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    private handleError(error: Response): Observable<any> {
        return throwError(
            error || "Server error, please refresh and try again"
        );
    }

    deleteContact(contactId: number): Observable<void> {
        this.showSpinner();
        return this.http.delete(this.apiUrl + "/" + contactId).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    getAllRoles(): Observable<Role[]> {
        this.showSpinner();
        return this.http.get(this.apiUrl + "/GetAllRoles").pipe(
            map((res) => res as Role[]),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    getTypes(): Observable<Lookup[]> {
        this.showSpinner();
        return this.http.get(this.apiUrl + "/getalltypes").pipe(
            map((res) => res as Lookup[]),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    getDisciplines(): Observable<Lookup[]> {
        this.showSpinner();
        return this.http.get(this.apiUrl + "/getalldisciplines").pipe(
            map((res) => res as Lookup[]),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    getAllInProject(projectId: number): Observable<Contact[]> {
        this.showSpinner();
        return this.http.get(this.apiUrl + "/getall/" + projectId).pipe(
            map((res) => res as Contact[]),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    getAllInOrganizationForRfi(
        projectId: number,
        entryNumber: number,
        name: string
    ): Observable<Contact[]> {
        this.showSpinner();
        return this.http
            .get(
                this.apiUrl +
                    "/" +
                    projectId +
                    "/rfi/" +
                    entryNumber +
                    "/" +
                    name
            )
            .pipe(
                map((res) => res as Contact[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getAllInOrganizationForCvi(
        projectId: number,
        entryNumber: number,
        name: string
    ): Observable<Contact[]> {
        this.showSpinner();
        return this.http
            .get(
                this.apiUrl +
                    "/" +
                    projectId +
                    "/cvi/" +
                    entryNumber +
                    "/" +
                    name
            )
            .pipe(
                map((res) => res as Contact[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getAllInOrganizationForSubmittal(
        projectId: number,
        entryNumber: number,
        name: string
    ): Observable<Contact[]> {
        this.showSpinner();
        return this.http
            .get(
                this.apiUrl +
                    "/" +
                    projectId +
                    "/submittal/" +
                    entryNumber +
                    "/" +
                    name
            )
            .pipe(
                map((res) => res as Contact[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getAllInOrganizationForDaywork(
        projectId: number,
        entryNumber: number,
        name: string
    ): Observable<Contact[]> {
        this.showSpinner();
        return this.http
            .get(
                this.apiUrl +
                    "/" +
                    projectId +
                    "/daywork/" +
                    entryNumber +
                    "/" +
                    name
            )
            .pipe(
                map((res) => res as Contact[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    delete(projectId: number, id: number): Observable<void> {
        this.showSpinner();
        return this.http.delete(this.apiUrl + "/" + projectId + "/" + id).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    private showSpinner() {
        this.globals.showSpinner();
    }

    private hideSpinner() {
        this.globals.hideSpinner();
    }
}

class Role {
    label: string;
    value: string;
}

export class Contact {
    contactNumber: string;
    firstName: string;
    lastName: string;
    country: string;
    city: string;
    province: string;
    email: string;
    role: string;
    id: number;
    fullName?: string;
    organizationName?: string;
    projectContactId?: number;
}

class Lookup {
    value: string;
    label: string;
}
